import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, MapPin, ShoppingBag } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { formatMXN } from "@/lib/format";

export default function CartDrawer({ cart }) {
    const navigate = useNavigate();
    const goCheckout = (storeSlug) => {
        cart.setOpen(false);
        navigate(`/checkout?store=${encodeURIComponent(storeSlug)}`);
    };

    return (
        <Sheet open={cart.open} onOpenChange={cart.setOpen}>
            <SheetContent
                data-testid="cart-drawer"
                className="w-full sm:max-w-md p-0 flex flex-col"
            >
                <SheetHeader className="px-6 py-5 border-b border-zinc-200">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <ShoppingBag className="w-4 h-4" />
                        Tu carrito
                        {cart.count > 0 && (
                            <span className="text-xs font-medium text-zinc-500">
                                ({cart.count} artículo{cart.count > 1 ? "s" : ""})
                            </span>
                        )}
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                        Productos agregados a tu carrito agrupados por tienda.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto">
                    {cart.groups.length === 0 ? (
                        <div className="h-full grid place-items-center px-6 py-20 text-center">
                            <div>
                                <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-zinc-100">
                                    <ShoppingBag className="w-6 h-6 text-zinc-400" />
                                </div>
                                <p className="mt-4 text-sm font-medium text-zinc-900">
                                    Tu carrito está vacío
                                </p>
                                <p className="mt-1 text-sm text-zinc-500">
                                    Agrega productos desde la tienda donde
                                    quieras comprar.
                                </p>
                                <Button
                                    data-testid="cart-empty-cta"
                                    onClick={() => cart.setOpen(false)}
                                    variant="outline"
                                    className="mt-5"
                                >
                                    Seguir viendo
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-zinc-200">
                            {cart.groups.map((group) => (
                                <div
                                    key={group.storeId}
                                    data-testid={`cart-group-${group.storeSlug}`}
                                    className="px-6 py-5"
                                >
                                    <div className="flex items-baseline justify-between mb-3">
                                        <Link
                                            to={`/${group.storeSlug}`}
                                            onClick={() => cart.setOpen(false)}
                                            className="text-sm font-semibold text-zinc-900 hover:text-indigo-600"
                                        >
                                            {group.storeName}
                                        </Link>
                                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {group.storeLocation?.city}
                                        </span>
                                    </div>
                                    <div className="space-y-4">
                                        {group.items.map((item) => (
                                            <CartLine
                                                key={item.key}
                                                item={item}
                                                cart={cart}
                                            />
                                        ))}
                                    </div>
                                    <div className="mt-4 flex items-center justify-between text-xs">
                                        <span className="text-zinc-500">
                                            Subtotal por tienda
                                        </span>
                                        <span className="font-semibold text-zinc-900">
                                            {formatMXN(group.subtotal)}
                                        </span>
                                    </div>
                                    <Button
                                        data-testid={`cart-checkout-${group.storeSlug}`}
                                        onClick={() => goCheckout(group.storeSlug)}
                                        className="w-full mt-3 bg-primary hover:bg-primary-hover text-primary-foreground h-10 font-medium"
                                    >
                                        Pagar — {group.storeName}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {cart.groups.length > 0 && (
                    <div className="border-t border-zinc-200 px-6 py-5 bg-zinc-50/60">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-500">Subtotal de productos</span>
                            <span className="font-semibold text-zinc-900">
                                {formatMXN(cart.subtotal)}
                            </span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                            Cada tienda se paga por separado. El envío se calcula con
                            Planeta Envíos al momento del checkout.
                        </p>
                        <button
                            data-testid="cart-continue-shopping"
                            onClick={() => cart.setOpen(false)}
                            className="w-full mt-3 text-sm text-zinc-500 hover:text-zinc-900 transition-colors py-1"
                        >
                            Seguir comprando
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function CartLine({ item, cart }) {
    return (
        <div className="flex gap-3" data-testid={`cart-line-${item.key}`}>
            <div className="w-16 h-16 rounded-md overflow-hidden bg-zinc-100 shrink-0">
                <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 line-clamp-2">
                    {item.name}
                </p>
                {item.variant && (
                    <p className="text-xs text-zinc-500 mt-0.5">
                        {item.variant}
                    </p>
                )}
                <div className="mt-2 flex items-center justify-between">
                    <div className="inline-flex items-center border border-zinc-200 rounded-md">
                        <button
                            data-testid={`cart-qty-decrement-${item.key}`}
                            onClick={() =>
                                cart.updateQty(item.key, item.quantity - 1)
                            }
                            className="w-7 h-7 grid place-items-center hover:bg-zinc-50 rounded-l-md"
                            aria-label="Disminuir"
                        >
                            <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-7 text-center text-xs font-medium">
                            {item.quantity}
                        </span>
                        <button
                            data-testid={`cart-qty-increment-${item.key}`}
                            onClick={() =>
                                cart.updateQty(item.key, item.quantity + 1)
                            }
                            className="w-7 h-7 grid place-items-center hover:bg-zinc-50 rounded-r-md"
                            aria-label="Aumentar"
                        >
                            <Plus className="w-3 h-3" />
                        </button>
                    </div>
                    <span className="text-sm font-semibold">
                        {formatMXN(item.price * item.quantity)}
                    </span>
                </div>
            </div>
            <button
                data-testid={`cart-remove-${item.key}`}
                onClick={() => cart.remove(item.key)}
                className="text-zinc-400 hover:text-red-500 transition-colors h-fit"
                aria-label="Eliminar"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
}
